import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  playRedDog,
  resolveRedDog,
  getRedDogHistory,
} from '../../../services/gameService';
import {
  Card,
  Button,
  InputGroup,
  FormControl,
  Spinner,
  Modal,
  Alert,
  Row,
  Col,
} from 'react-bootstrap';
import {
  FaCoins,
  FaPlayCircle,
  FaArrowLeft,
  FaHistory,
  FaInfoCircle,
  FaGamepad,
  FaTrophy,
  FaRegClock,
  FaRedoAlt,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './RedDog.css';

/**
 * Fases:
 *  - 'start'     -> No hay jugada iniciada (botón Jugar activo)
 *  - 'decision'  -> Dos cartas visibles + botones Raise / Fold
 *  - 'resolved'  -> Resultado final (muestra 3ª carta si aplica) + "Jugar de nuevo"
 */
const RedDog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Apuesta
  const [betAmount, setBetAmount] = useState(1);

  // Estado de la ronda
  const [phase, setPhase] = useState('start'); // 'start' | 'decision' | 'resolved'
  const [card1, setCard1] = useState(null);
  const [card2, setCard2] = useState(null);
  const [card3, setCard3] = useState(null);
  const [handType, setHandType] = useState(null); // 'pair' | 'consecutive' | 'valid'
  const [requiresDecision, setRequiresDecision] = useState(false);

  // Resultado
  const [gameResult, setGameResult] = useState(''); // 'win' | 'lose' | 'draw'
  const [newBalance, setNewBalance] = useState(null);

  // Mensajes/errores
  const [serverMsg, setServerMsg] = useState('');
  const [error, setError] = useState('');

  // Cargas
  const [playing, setPlaying] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [fetchingHistory, setFetchingHistory] = useState(false);

  // Historial / Reglas
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const getCardImage = (card) =>
    card ? `${process.env.PUBLIC_URL}/images/games/cards/${card}.png` : '';

  const resetRound = () => {
    setPhase('start');
    setCard1(null);
    setCard2(null);
    setCard3(null);
    setHandType(null);
    setRequiresDecision(false);
    setGameResult('');
    setNewBalance(null);
    setServerMsg('');
    setError('');
  };

  // Lógica: si la mano no requiere decisión (pair / consecutive), auto-resolvemos
  const autoResolve = async () => {
    try {
      const auto = await resolveRedDog('raise'); // acción ignorada en backend para casos auto
      setCard3(auto.card3 || null);
      setGameResult(auto.result);
      setNewBalance(auto.newBalance);
      setPhase('resolved');
    } catch (err) {
      setError(err?.response?.data?.error || t('redDog.error'));
      setPhase('resolved');
    }
  };

  // Fase 1: iniciar jugada
  const handlePlay = async () => {
    // Validación rápida de apuesta
    if (
      typeof betAmount !== 'number' ||
      isNaN(betAmount) ||
      betAmount <= 0 ||
      betAmount > 1_000_000
    ) {
      setError(t('redDog.error'));
      return;
    }

    setPlaying(true);
    setError('');
    setServerMsg('');
    try {
      const res = await playRedDog(betAmount);

      setCard1(res.card1);
      setCard2(res.card2);
      setCard3(null);
      setGameResult('');
      setNewBalance(null);

      setHandType(res.handType || null);
      setRequiresDecision(!!res.requiresDecision);
      setServerMsg(res.message || '');

      if (res.requiresDecision) {
        setPhase('decision'); // jugador elige Raise / Fold
      } else {
        // Resultado automático (pair / consecutive)
        await autoResolve();
      }
    } catch (err) {
      setError(err?.response?.data?.error || t('redDog.error'));
      setPhase('start');
    } finally {
      setPlaying(false);
    }
  };

  // Fase 2: jugador decide Raise o Fold
  const handleDecision = async (action) => {
    if (!requiresDecision || (action !== 'raise' && action !== 'fold')) return;

    setResolving(true);
    setError('');
    try {
      const res = await resolveRedDog(action);
      setCard3(res.card3 || null);
      setGameResult(res.result);
      setNewBalance(res.newBalance);
      setPhase('resolved');
    } catch (err) {
      setError(err?.response?.data?.error || t('redDog.error'));
    } finally {
      setResolving(false);
    }
  };

  // Historial
  const handleShowHistory = async () => {
    setFetchingHistory(true);
    setError('');
    try {
      const data = await getRedDogHistory();
      setHistory(Array.isArray(data) ? data : []);
      setShowHistory(true);
    } catch (err) {
      setError(t('redDog.history_error'));
    } finally {
      setFetchingHistory(false);
    }
  };

  return (
    <div className="red-dog-container">
      <Card className="text-center red-dog-card">
        <Card.Body>
          {/* Header */}
          <div className="position-relative mb-3 d-flex align-items-center">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              className="me-auto"
            >
              <FaArrowLeft className="me-2" />
              {/* Puedes traducir el texto del botón si quieres */}
            </Button>
            <h1 className="red-dog-title m-0">{t('redDog.title')}</h1>
            <div className="ms-auto" />
          </div>

          {/* Apuesta */}
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <FaCoins />
            </InputGroup.Text>
            <FormControl
              type="number"
              min={1}
              max={1_000_000}
              value={betAmount}
              onChange={(e) => setBetAmount(Number(e.target.value))}
            />
            <InputGroup.Text>MENEL</InputGroup.Text>
          </InputGroup>

          {/* Botonera superior */}
          <div className="d-flex justify-content-center flex-wrap gap-2 mb-2">
            {phase === 'start' && (
              <Button
                className="red-dog-play-button"
                onClick={handlePlay}
                disabled={playing}
              >
                {playing ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaPlayCircle className="me-2" />
                    {t('redDog.play')}
                  </>
                )}
              </Button>
            )}

            {phase === 'resolved' && (
              <Button
                variant="outline-primary"
                onClick={resetRound}
                disabled={playing || resolving}
              >
                <FaRedoAlt className="me-2" />
                {t('redDog.play')}
              </Button>
            )}

            <Button
              variant="info"
              onClick={handleShowHistory}
              disabled={fetchingHistory || playing || resolving}
            >
              <FaHistory className="me-2" />
              {t('redDog.history')}
            </Button>

            <Button
              variant="warning"
              onClick={() => setShowRules(true)}
              disabled={playing || resolving}
            >
              <FaInfoCircle className="me-2" />
              {t('redDog.rules')}
            </Button>
          </div>

          {/* Mensajes / Errores */}
          {serverMsg && !error && (
            <div className="text-muted">{serverMsg}</div>
          )}
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Cartas */}
          {(card1 || card2 || card3) && (
            <Row className="justify-content-center align-items-start mt-3 g-4">
              <Col xs={12} md={4}>
                <h5 className="mb-2">{t('redDog.card1')}</h5>
                {card1 && (
                  <img
                    className="red-dog-card-img"
                    src={getCardImage(card1.card)}
                    alt={card1.card}
                  />
                )}
              </Col>

              <Col xs={12} md={4}>
                <h5 className="mb-2">{t('redDog.card2')}</h5>
                {card2 && (
                  <img
                    className="red-dog-card-img"
                    src={getCardImage(card2.card)}
                    alt={card2.card}
                  />
                )}
              </Col>

              {phase === 'resolved' && card3 && (
                <Col xs={12} md={4}>
                  <h5 className="mb-2">{t('redDog.card3')}</h5>
                  <img
                    className="red-dog-card-img"
                    src={getCardImage(card3.card)}
                    alt={card3.card}
                  />
                </Col>
              )}
            </Row>
          )}

          {/* Botones de decisión (solo si el servidor dijo que hay decisión) */}
          {phase === 'decision' && requiresDecision && (
            <div className="d-flex justify-content-center gap-3 mt-3">
              <Button
                variant="success"
                onClick={() => handleDecision('raise')}
                disabled={resolving}
              >
                {resolving ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t('redDog.raise')
                )}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDecision('fold')}
                disabled={resolving}
              >
                {t('redDog.fold')}
              </Button>
            </div>
          )}

          {/* Resultado y saldo */}
          {phase === 'resolved' && gameResult && (
            <div className="mt-4" aria-live="polite">
              <h3
                className={`red-dog-result ${
                  gameResult === 'win'
                    ? 'text-success'
                    : gameResult === 'draw'
                    ? 'text-warning'
                    : 'text-danger'
                }`}
              >
                {gameResult === 'win' ? <FaTrophy /> : <FaRegClock />}{' '}
                {t(`redDog.result.${gameResult}`) || gameResult}
              </h3>
              {newBalance !== null && (
                <p className="red-dog-balance">
                  {t('redDog.new_balance', { amount: newBalance })}
                </p>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal Historial */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('redDog.history')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {fetchingHistory ? (
            <Spinner animation="border" />
          ) : history.length > 0 ? (
            <ul className="list-group">
              {history.map((entry, idx) => (
                <li key={idx} className="list-group-item">
                  <strong>{t('redDog.result_label')}:</strong> {entry.result}
                  <br />
                  <strong>{t('redDog.score')}:</strong> {entry.score}
                  <br />
                  <strong>{t('redDog.details')}:</strong> {entry.details}
                  <br />
                  <strong>{t('redDog.date')}:</strong>{' '}
                  {new Date(entry.created_at).toLocaleString()}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">{t('redDog.history_error')}</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal Reglas */}
      <Modal show={showRules} onHide={() => setShowRules(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaGamepad className="me-2 text-warning" />
            {t('redDog.rules')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-2">{t('redDog.rules_title')}</h5>
          <p>{t('redDog.rules_description')}</p>
          <ul>
            <li>{t('redDog.rules_bet')}</li>
            <li>{t('redDog.rules_cards')}</li>
            <li>{t('redDog.rules_predict')}</li>
            <li>{t('redDog.rules_win')}</li>
          </ul>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RedDog;
